<template>
  <div class="csr-signature-view container">
    <div class="breadcrumb-password-view pt-3 row">
      <Breadcrumb
        :path-list="{
          'Guia de instalação SSL': 'ssl-certificate/installation-guide-ssl',
          'Downolad de Certificado Digital com CSR': '',
        }"
      />
    </div>

    <div class="title text-start">
      <h2 class="fw-600">
        Instalação de Certificado Digital Financeiro e SSL com CSR
      </h2>
    </div>

    <div class="subtitle text-start">
      <p class="fs-6">
        Preencha os campos abaixo para finalizar o processo de emissão e
        instalar seu Certificado Digital.
      </p>
    </div>

    <div class="content-password-view">
      <div class="d-flex flex-row row">
        <div class="col-12 col-lg-6 d-none d-lg-block text-center">
          <img
            src="@/assets/images/instalar-certificado-ssl.svg"
            alt="emitir-certificado"
          />
        </div>
        <div class="col-12 col-lg-6 text-start">
          <div class="mb-3">
            <strong> Usuário: {{ sslCertificate.user || '' }} </strong>
          </div>

          <TextArea
            v-model="model.csr"
            class="mb-2"
            label="CSR"
            rows="4"
            :is-required="true"
            size="160"
            :resizable="false"
            placeholder="Cole aqui o CSR"
            :error-log="errors.csr"
          />

          <router-link
            class="link-info mb-5"
            to="/faq/generate-csr-request"
            target="_blank"
          >
            <span class="me-2">Instruções para gerar CSR</span>
            <Icon name="arrow-up-right-from-square" />
          </router-link>

          <div v-if="hastermDigitalHash">
            <strong>Você também precisará assinar o Termo Digital.</strong>
            <p>
              Faça download do hash do Termo e cole a assinatura no campo abaixo
            </p>

            <router-link
              class="link-info mb-5"
              to="/faq/sign-digital-term"
              alt="faq"
            >
              <span class="me-2"> Instruções para Assinar o Termo Digital</span>
              <Icon name="arrow-up-right-from-square" />
            </router-link>

            <button
              class="btn btn-outline-primary col-12 col-lg-10 col-xl-7 mb-5"
              type="button"
              @click="downloadHash"
            >
              <Icon class="me-2" name="download" />
              <span> Download do hash do Termo Digital</span>
            </button>

            <TextArea
              id="csr"
              v-model="model.signedHash"
              name="csr"
              class="mb-5"
              label="Assinatura do Termo Digital"
              rows="6"
              :is-required="true"
              size="160"
              :resizable="false"
              placeholder="Insira aqui o hash do Termo Digital assinado"
              :error-log="errors.signedHash"
            />
          </div>

          <div class="d-flex justify-content-end mt-2">
            <button
              id="emit-certificate-button"
              class="btn"
              type="button"
              :class="!isValid ? 'btn-secondary' : 'btn-primary'"
              :disabled="isDisaled"
              @click="handleSubmit"
            >
              <span> Emitir Certificado </span>
              <Icon class="ms-2" name="shield"></Icon>
            </button>
          </div>
        </div>
      </div>
      <Contact />
    </div>
  </div>
  <Loading :is-loading="isLoading" />
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Contact from '@/components/Molecules/Contact.vue'
  import Icon from '@/components/Atoms/Icon.vue'
  import TextArea from '@/components/Atoms/TextArea.vue'
  import Breadcrumb from '@/components/Molecules/Breadcrumbs.vue'
  import CRSValidation from '@/utils/CRSValidation'
  import { mapState, mapActions } from 'pinia'
  import { useSSLCertificate } from '@/store/Pinia/useSSLCertificate.store'
  import { useDynamicForms } from '@/store/Pinia/useDynamicForms.store'
  import { useAR } from '@/store/Pinia/useAR.store'
  import { useToastGlobal } from '@/store/Pinia/useToastGlobal.store'
  import Loading from '@/components/Atoms/Loading.vue'
  import { panelController } from '@/infra/SSLCertificate/panel.controller'
  import { EPanel } from '@/types/enums/EPanel.enum'
  import formatCSR from '@/utils/formatCSR'
  import { useOeRecaptcha } from '@/composables/useOeRecaptcha'
  // import formatCSR from '@/utils/formatCSR';

  export default defineComponent({
    name: 'HomeView',
    components: {
      Contact,
      Icon,
      TextArea,
      Breadcrumb,
      Loading,
    },
    setup() {
      return {
        oeRecaptcha: useOeRecaptcha(),
      }
    },
    data: () => ({
      model: {
        csr: '',
        signedHash: '',
      },
      errors: {
        csr: '',
        signedHash: '',
      },
      validationVerifier: null as ReturnType<typeof setInterval> | null,
      isLoading: false,
    }),
    computed: {
      ...mapState(useSSLCertificate, ['sslCertificate']),
      ...mapState(useDynamicForms, ['getPassword']),
      ...mapState(useAR, ['arData']),
      isDisaled() {
        if (!this.hastermDigitalHash) {
          return this.model.csr === ''
        }

        return this.model.csr === '' || this.model.signedHash === ''
      },
      isValid() {
        return (
          !Object.values(this.errors).find((x) => x !== '') && !this.isDisaled
        )
      },
      hastermDigitalHash() {
        return this.sslCertificate.digitaltermHash !== ''
      },
      pathlist() {
        return {
          'Guia de instalação SSL': 'ssl-certificate/installation-guide-ssl',
          'Downolad de Certificado Digital com CSR': '',
        }
      },
    },
    watch: {
      'model.csr': function w(curr) {
        if (!CRSValidation(curr)) {
          this.errors.csr = 'Insira um CSR válido'
        } else {
          this.errors.csr = ''
        }
      },
      'model.signedHash': function w() {
        this.errors.signedHash = ''
      },
    },
    methods: {
      ...mapActions(useToastGlobal, ['toggleToastGlobal']),
      handleSubmit() {
        if (this.hastermDigitalHash) {
          return this.submitWithHash()
        }

        return this.submitWithoutHash()
      },
      async submitWithHash() {
        try {
          if (
            this.isValid &&
            this.model.csr !== '' &&
            this.model.signedHash !== ''
          ) {
            const model = {
              solicitacao: this.sslCertificate.user,
              senha: this.getPassword,
              csr: formatCSR(this.model.csr),
              digitaltermSignature: this.model.signedHash,
              arsubdomain: this.arData.subdomain || '',
            }

            this.isLoading = true

            const recaptchaTokenEmit = await this.oeRecaptcha.getToken('emit')

            await panelController.emit({
              ...model,
              recaptcha_token: recaptchaTokenEmit,
            })

            this.validationVerifier = setInterval(async () => {
              await this.verifyValidation()
            }, 5000)
          }

          this.errors.signedHash =
            'É necessário inserir o hash do Termo Digital assinado.'
        } catch (error: any) {
          const backendMessage =
            error?.response?.data?.mensagem?.erro?.mensagem ||
            error?.response?.data?.message ||
            error?.response?.data?.mensagem ||
            error?.message
          this.errors.signedHash =
            backendMessage || 'Erro ao enviar o HASH assinado'
        }
      },
      async submitWithoutHash() {
        try {
          if (this.isValid && this.model.csr !== '') {
            const model = {
              solicitacao: this.sslCertificate.user,
              senha: this.getPassword,
              csr: formatCSR(this.model.csr),
              digitaltermSignature: '',
              arsubdomain: this.arData.subdomain || '',
            }

            this.isLoading = true

            await this.verifyValidation()

            const recaptchaTokenEmit = await this.oeRecaptcha.getToken('emit')

            const responseEmit = await panelController.emit({
              ...model,
              recaptcha_token: recaptchaTokenEmit,
            })

            if (responseEmit.mensagem.erro) {
              this.isLoading = false
              throw new Error(responseEmit.mensagem.erro?.mensagem)
            }

            this.validationVerifier = setInterval(async () => {
              await this.verifyValidation()
            }, 5000)
          }
        } catch (error: any) {
          const backendMessage =
            error?.response?.data?.mensagem?.erro?.mensagem ||
            error?.response?.data?.message ||
            error?.response?.data?.mensagem ||
            error?.message
          this.toggleToastGlobal({
            isShow: true,
            message: backendMessage || 'Erro ao emitir o certificado',
          })
          clearInterval(this.validationVerifier)
        }
      },
      async verifyValidation() {
        try {
          const recaptchaTokenGetSolicitationData =
            await this.oeRecaptcha.getToken('panelvalidate')

          const response = await panelController.validate({
            password: this.getPassword,
            solicitation: this.sslCertificate.user,
            arsubdomain: this.arData.subdomain || '',
            recaptcha_token: recaptchaTokenGetSolicitationData,
          })

          if (response.mensagem.emissao) {
            if (
              response.mensagem.emissao.status ===
              EPanel.EStatus.CERTIFICADO_JA_EMITIDO
            ) {
              this.$router.push('/ssl-certificate/csr-download')
              clearInterval(this.validationVerifier)
            }

            if (
              response.mensagem.emissao.status ===
              EPanel.EStatus.ERRO_SOLICITACAO
            ) {
              this.isLoading = false
              clearInterval(this.validationVerifier)
              const errorDefault =
                'Erro ao validar informações, tente novamente mais tarde'
              throw new Error(response.mensagem.emissao.obs || errorDefault)
            }
          }
        } catch (error: any) {
          const backendMessage =
            error?.response?.data?.mensagem?.erro?.mensagem ||
            error?.response?.data?.message ||
            error?.response?.data?.mensagem ||
            error?.message
          this.toggleToastGlobal({
            isShow: true,
            message:
              backendMessage ||
              'Erro ao validar informações, tente novamente mais tarde',
          })
          clearInterval(this.validationVerifier)
        }
      },
      downloadHash() {
        const blob = new Blob([this.sslCertificate.digitaltermHash])
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = 'digitalterm_hash.hex'
        link.click()
        link.remove()
      },
      goToCsrInstructions() {
        const routeData = this.$router.resolve({
          path: '/faq/generate-csr-request',
        })
        window.open(routeData.href, '_blank')
      },
    },
    async beforeRouteEnter(to, from, next) {
      const sslStore = useSSLCertificate()
      const dynamicFormsStore = useDynamicForms()

      const { user } = sslStore.$state.sslCertificate
      const { senha } = dynamicFormsStore.$state.formData

      if (user === '' && senha === '') {
        return next('/ssl-certificate/login-ssl')
      }

      return next()
    },
    mounted() {
      this.oeRecaptcha.showBadge()
    },
    beforeRouteLeave() {
      this.oeRecaptcha.hideBadge()
    },
  })
</script>

<style lang="stylus" scoped>
  .title
    font-size: 24px
    font-weight: 500
    padding-bottom: 0.5rem
  .subtitle
    padding-bottom: 56px

  @media screen and (max-width: 992px)
    #emit-certificate-button
      width: 100%
</style>
